// Antes de cobrar: el silo rechaza el cobro con líneas PENDING o RELEASING. Las RELEASING se
// reintentan de liberar una vez al entrar; «retry» lo repite a mano.
import { useEffect, useRef } from 'react';
import { unchargeableLinesOf } from '../saleCharge';
import type { WizardSession } from '../types';
import { useReleaseProduct } from './useWizard';

export interface ChargeableLines {
    /** Hay alguna línea que impide cobrar. */
    blocked: boolean;
    /** Líneas a medio liberar. */
    releasingCount: number;
    /** Líneas a medio apartar. */
    pendingCount: number;
    /** Líneas que Zentral se llevó: se cambian en Arma la venta. */
    lostCount: number;
    retrying: boolean;
    retry: () => Promise<void>;
}

export function useChargeableLines(
    sessionId: string,
    session: WizardSession | undefined,
): ChargeableLines {
    const release = useReleaseProduct(sessionId);
    const stuck = session ? unchargeableLinesOf(session) : [];
    const releasingIds = stuck.filter((l) => l.status === 'RELEASING').map((l) => l.lineId);
    const lostCount = stuck.filter((l) => l.status === 'LOST').length;
    const tried = useRef(new Set<string>());

    const releaseAll = async (ids: string[]): Promise<void> => {
        for (const lineId of ids) {
            try {
                await release.mutateAsync({ lineId });
            } catch {
                // Sigue RELEASING: el aviso queda en pantalla con su «Reintentar».
            }
        }
    };

    const key = releasingIds.join(',');
    useEffect(() => {
        const fresh = releasingIds.filter((id) => !tried.current.has(id));
        if (fresh.length === 0) return;
        fresh.forEach((id) => tried.current.add(id));
        void releaseAll(fresh);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- un intento automático por línea
    }, [key]);

    return {
        blocked: stuck.length > 0,
        releasingCount: releasingIds.length,
        pendingCount: stuck.length - releasingIds.length - lostCount,
        lostCount,
        retrying: release.isPending,
        retry: () => releaseAll(releasingIds),
    };
}
